import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Booking, getBooking, deleteBooking, updateBooking } from '../lib/bookings';
import { createBookingCompletedMilestone } from '../lib/bookingMilestones';
import type { BookingResource, BookingService } from '../types/bookings';
import { deleteDiscount } from '../lib/discounts';
import { Material } from '../lib/materials';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase'; 
import BookingHeader from './booking/BookingHeader';
import BookingStatusBar from './booking/BookingStatusBar';
import BookingTabs from './booking/BookingTabs';
import MobileTabStatusHeader from './booking/MobileTabStatusHeader';
import BookingContent from './booking/BookingContent';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import ModifyCustomerModal from './ModifyCustomerModal';
import SelectMaterialModal from './manage/SelectMaterialModal';
import EditVehicleModal from './booking/EditVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectResourceModal from './manage/SelectResourceModal';
import ApplyDiscountModal from './ApplyDiscountModal';
import EditTitleModal from './booking/EditTitleModal';
import BookingColorPickerModal from './BookingColorPickerModal';
import CreateVehicleModal from './CreateVehicleModal';
import type { Customer, CustomerVehicle } from '../lib/customers';
import { getCustomer } from '../lib/customers';
import type { Payment } from '../types/payments';
import { processCanceledBooking } from '../services/canceledBookingProcessor';
import { processBookingStatusChange } from '../services/bookingStatusProcessor';
import { checkBookingStatusTriggers, checkBookingStatus } from '../services/debugHelper';
import { deleteFeedbackForBooking } from '../lib/feedback';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { useModalPersistence } from '../utils/modalPersistence';
import { usePermissionCheck } from '../hooks/usePermissionCheck';

interface BookingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
  onUpdate: () => void;
  readOnly?: boolean;
}

// Debug flag to control logging - set to false to disable verbose logs
const enableInfoModalDebug = false;

export default function BookingInfoModal({ 
  isOpen, 
  onClose, 
  booking, 
  onUpdate,
  readOnly = false
}: BookingInfoModalProps) {
  const { selectedBusiness } = useBusiness();
  const { canAccess } = usePermissionCheck();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [permissionLevel, setPermissionLevel] = useState<'manage' | 'view' | 'disabled'>('manage');
  const [error, setError] = useState<string | null>(null);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [selectedFlatRate, setSelectedFlatRate] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [currentVehicleId, setCurrentVehicleId] = useState<string | null>(null);
  const [localBooking, setLocalBooking] = useState<Booking | null>(null);
  const [scheduleChanged, setScheduleChanged] = useState(false);
  const [currentVehicle, setCurrentVehicle] = useState<any>(null);
  const [isSelectMaterialModalOpen, setIsSelectMaterialModalOpen] = useState(false);
  const [isApplyDiscountModalOpen, setIsApplyDiscountModalOpen] = useState(false);
  const [isSelectFlatRateModalOpen, setIsSelectFlatRateModalOpen] = useState(false);
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  const [isBookingColorPickerModalOpen, setIsBookingColorPickerModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Modal persistence hook
  const { saveModalState, clearModalState, updateActiveTab, updateBookingData, getModalState } = useModalPersistence();
  
  // Reference to the modal portal div
  const modalPortalRef = useRef<HTMLDivElement | null>(null);
  
  // Create a new portal for iOS devices
  useEffect(() => {
    // Only create portal if we're on iOS and modal is open
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS || !isOpen) return;
    
    // Create modal portal if it doesn't exist yet
    if (!document.getElementById('booking-modal-portal')) {
      const portalDiv = document.createElement('div');
      portalDiv.id = 'booking-modal-portal';
      // Set portal styles
      portalDiv.style.position = 'fixed';
      portalDiv.style.top = '0';
      portalDiv.style.left = '0';
      portalDiv.style.width = '100%';
      portalDiv.style.height = '100%';
      portalDiv.style.zIndex = '99999';
      portalDiv.style.backgroundColor = 'white';
      // Append to body
      document.body.appendChild(portalDiv);
      modalPortalRef.current = portalDiv;
    } else {
      modalPortalRef.current = document.getElementById('booking-modal-portal') as HTMLDivElement;
    }
    
    // Cleanup function - run when either component unmounts or when isOpen becomes false
    return () => {
      if (modalPortalRef.current) {
        try {
          document.body.removeChild(modalPortalRef.current);
          modalPortalRef.current = null;
        } catch (e) {
          // Portal already removed, ignore
        }
      }
    };
  }, [isOpen]);

  // Reset body styles when the modal closes to prevent blank page issue
  useEffect(() => {
    if (!isOpen) {
      // Reset all body styles to defaults
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.classList.remove('modal-open-ios');
      document.body.classList.remove('modal-open');
    }
  }, [isOpen]);



  // Handle window resize events for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAddResource = async (resource: any) => {
    try {
      setLoading(true);
      setError(null);

      if (!resource || !resource.id) {
        throw new Error('Invalid resource selected');
      }

      // Get current resources and add the new one
      const currentResources = localBooking?.resources || [];
      const updatedResources = [
        ...currentResources.map(r => ({
          resource_id: r.resource_id,
          role: r.role,
          service_id: r.service_id,
          start_date: r.start_date,
          end_date: r.end_date
        })),
        {
          resource_id: resource.id === 'unassigned' ? null : resource.id,
          role: 'technician',
          service_id: null,
          start_date: null,
          end_date: null
        }
      ];
      
      // Update the booking with the new resources
      if (localBooking?.id) {
        await updateBooking(localBooking.id, { resources: updatedResources });
        // Update selectedResources state with the new resource
        setSelectedResources(prev => [...prev, resource.id]);
        await refreshBooking();
        setIsSelectResourceModalOpen(false);
      } else {
        throw new Error('No booking ID found');
      }
    } catch (err) {
      console.error('Error adding resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to add resource');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveResource = async (resourceId: string) => {
    try {
      setLoading(true);
      if (!localBooking?.id) {
        throw new Error('No booking ID found');
      }

      // Filter out the resource to remove and map to the correct structure
      const updatedResources = localBooking.resources
        ?.filter(r => r.resource_id !== resourceId)
        .map(r => ({
          resource_id: r.resource_id,
          role: r.role,
          service_id: r.service_id,
          start_date: r.start_date,
          end_date: r.end_date
        })) || [];

      await updateBooking(localBooking.id, { resources: updatedResources });
      // Update selectedResources state by removing the resource
      setSelectedResources(prev => prev.filter(id => id !== resourceId));
      await refreshBooking();
    } catch (err) {
      console.error('Error removing resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove resource');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && booking?.id) {
      // More aggressive body style changes to fix iOS stacking issues
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed'; // Force fixed positioning
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      // Higher z-index class for iOS issues
      document.body.classList.add('modal-open-ios');
      document.body.classList.add('modal-open');
      setError(null);
      loadBookingData(booking.id);

      // Restore active tab from saved state if available
      const savedState = getModalState();
      if (savedState && savedState.activeTab && savedState.bookingId === booking.id) {
        console.log('📱 Restoring active tab:', savedState.activeTab);
        setActiveTab(savedState.activeTab);
      }

      // Save modal state to localStorage when it opens
      console.log('📱 Modal opened, saving state to localStorage');
      saveModalState({
        isOpen: true,
        bookingId: booking.id,
        bookingData: booking,
        activeTab: savedState?.activeTab || activeTab
      });
    } else if (!isOpen) {
      // Clear modal state when it closes
      console.log('📱 Modal closed, clearing localStorage state');
      clearModalState();
    }
  }, [isOpen, booking?.id]);

  // Save active tab changes to localStorage
  useEffect(() => {
    if (isOpen && booking?.id) {
      updateActiveTab(activeTab);
    }
  }, [activeTab, isOpen, booking?.id, updateActiveTab]);

  // Save booking data changes to localStorage
  useEffect(() => {
    if (isOpen && localBooking) {
      updateBookingData(localBooking);
    }
  }, [localBooking, isOpen, updateBookingData]);



  const loadBookingData = async (bookingId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading booking data:', {
        bookingId,
        hasCustomer: !!booking?.customer,
        customerId: booking?.customer?.id
      });

      const freshBooking = await getBooking(bookingId);
      
      if (!freshBooking) {
        throw new Error(`Booking ${bookingId} not found`);
      }
      
      if (!freshBooking.customer) {
        throw new Error(`No customer found for booking ${bookingId}`);
      }

      if (!freshBooking.customer.id) {
        throw new Error(`Invalid customer ID for booking ${bookingId}`);
      }

      console.log('Booking data loaded:', {
        bookingId: freshBooking.id,
        customerId: freshBooking.customer.id,
        customerName: `${freshBooking.customer.first_name} ${freshBooking.customer.last_name}`,
        services: freshBooking.services?.length
      });

      setLocalBooking(freshBooking);
      // Update selectedResources with current booking resources
      if (freshBooking.resources) {
        const resourceIds = freshBooking.resources.map(r => r.resource_id).filter((id): id is string => id !== null);
        setSelectedResources(resourceIds);
      } else {
        setSelectedResources([]);
      }
    } catch (err) {
      console.error('Error loading booking:', {
        error: err,
        bookingId,
        customerId: booking?.customer?.id
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to load booking details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setError(null);
    }
  };

  const refreshBooking = async () => {
    if (!localBooking?.id) {
      console.error('Cannot refresh booking: No booking ID', {
        booking: localBooking
      });
      return;
    }
    
    await loadBookingData(localBooking.id);
    // Call parent's onUpdate to refresh the entire booking list
    onUpdate();
  };

  // Optimized address update function to prevent loading flash
  const handleAddressUpdate = async (address: string) => {
    if (!localBooking?.id) return;

    try {
      // Update local state immediately to prevent UI flash
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          booking_address: address
        } as Booking;
      });

      // Update database in background
      await updateBooking(localBooking.id, { booking_address: address });
      
      // Call parent's onUpdate to refresh the calendar/list view
      onUpdate();
    } catch (err) {
      console.error('Error updating booking address:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking address');
      
      // Revert local state on error
      if (localBooking) {
        setLocalBooking(prev => {
          if (!prev) return null;
          return {
            ...prev,
            booking_address: localBooking.booking_address
          } as Booking;
        });
      }
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!localBooking?.id) return;

    try {
      setStatusLoading(true);
      setError(null);

      // Store the previous status before updating
      const previousStatus = localBooking.status;
      
      console.log(`[STATUS] Updating booking ${localBooking.id} from ${previousStatus} to ${newStatus}`);

      // Handle feedback token removal when changing from completed to another status
      if (previousStatus === 'completed' && newStatus !== 'completed') {
        try {
          console.log(`[STATUS] 🗑️ Removing feedback token for booking changing from completed to ${newStatus}`);
          const feedbackDeleted = await deleteFeedbackForBooking(localBooking.id);
          if (feedbackDeleted) {
            console.log(`[STATUS] ✅ Feedback token removed successfully`);
          } else {
            console.warn(`[STATUS] ⚠️ Failed to remove feedback token, continuing with status change`);
          }
        } catch (error) {
          console.error(`[STATUS] ❌ Error removing feedback token:`, error);
          // Don't fail the status change if feedback removal fails
        }
      }

      // Update completed_at timestamp for completed bookings
      let completed_at = localBooking.completed_at;
      if (newStatus === 'completed' && !completed_at) {
        completed_at = new Date().toISOString();
      } else if (newStatus !== 'completed') {
        completed_at = undefined; // Clear completed_at for non-completed statuses
      }

      const result = await updateBooking(localBooking.id, {
        status: newStatus,
        completed_at
      });

      if (!result) {
        throw new Error('Failed to update booking status');
      }

      // Update local state immediately to reflect the change in UI
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: newStatus,
          completed_at: completed_at || prev.completed_at,
          // Clear feedback_token if changing from completed to another status
          feedback_token: (previousStatus === 'completed' && newStatus !== 'completed') ? null : prev.feedback_token
        } as Booking;
      });

      // Create booking completed milestone when status changes to completed
      if (newStatus === 'completed' && previousStatus !== 'completed') {
        try {
          await createBookingCompletedMilestone(
            localBooking.business_id,
            localBooking.customer?.id || '',
            localBooking.id,
            localBooking.title,
            completed_at
          );
          console.log('✅ Booking completed milestone created successfully');
        } catch (milestoneError) {
          console.error('❌ Error creating booking completed milestone:', milestoneError);
          // Continue even if milestone creation fails
        }
      }

      // If the status is one that triggers automations, process it
      if (newStatus === 'in_progress' || newStatus === 'completed') {
        try {
          // Process the status change to trigger OLD automation system
          console.log(`[STATUS] 🚀 Triggering OLD automation processing for status: ${previousStatus} → ${newStatus}`);
          const processingResult = await processBookingStatusChange(localBooking.id, newStatus, previousStatus);
          
          if (processingResult.success) {
            console.log(`[STATUS] ✅ OLD automation processing completed successfully`);
          } else {
            console.warn(`[STATUS] ⚠️ OLD automation processing had issues: ${processingResult.error}`);
          }
        } catch (error) {
          console.error(`[STATUS] ❌ Error in OLD automation processing:`, error);
        }

        // 🎯 Also trigger NEW QStash-based automations for in_progress and completed status changes
        if (selectedBusiness?.id && localBooking) {
          try {
            console.log(`[STATUS] 🚀 Triggering NEW QStash automations for status: ${previousStatus} → ${newStatus}`);
            
                         // Prepare booking data for automation trigger
             const realBookingData = {
               booking_id: localBooking.id,
               booking_number: localBooking.booking_number,
               title: localBooking.title,
               customer: {
                 full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
                 phone: localBooking.customer?.phone || undefined,
                 email: localBooking.customer?.email || undefined
               },
               schedule: {
                 drop_off_date: localBooking.drop_off_date || '',
                 drop_off_time: localBooking.drop_off_time || '',
                 start_date: localBooking.start_date || '',
                 start_time: localBooking.start_time || '',
                 end_date: localBooking.end_date || '',
                 end_time: localBooking.end_time || ''
               },
               services: (localBooking.services || []).map(service => ({
                 name: service.name,
                 price: service.price,
                 duration_minutes: service.duration_minutes || 60,
                 vehicle: {
                   make: service.vehicle_make || '',
                   model: service.vehicle_model || '',
                   year: service.vehicle_year || 0,
                   color: service.vehicle_color || ''
                 }
               })),
               vehicles: (() => {
                 const vehicles = (localBooking.services || [])
                   .filter(service => service.vehicle_make || service.vehicle_model)
                   .map(service => ({
                     make: service.vehicle_make || '',
                     model: service.vehicle_model || '', 
                     year: service.vehicle_year || 0,
                     color: service.vehicle_color || '',
                     display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
                   }));
                 
                 // Remove duplicates based on make/model/year combination
                 const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                   index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
                 );
                 
                 return uniqueVehicles;
               })(),
               grand_total: (localBooking as any).grand_total || localBooking.subtotal || 0,
               status: newStatus,
               resources: (localBooking.resources || []).map(bookingResource => ({
                 name: `${bookingResource.resource?.first_name || ''} ${bookingResource.resource?.last_name || ''}`.trim(),
                 email: bookingResource.resource?.email || '',
                 phone: bookingResource.resource?.phone || ''
               })),
               feedback_token: newStatus === 'completed' ? localBooking.feedback_token : undefined,
               source: 'status_change'
             };

            // Map status to event type for automation system
            const eventType = newStatus === 'in_progress' ? 'in_progress' : 'completed';
            
            console.log(`🎯 STATUS CHANGE: Triggering QStash automations with data:`, realBookingData);
            await triggerBookingAutomations(selectedBusiness.id, eventType, realBookingData);
            console.log(`🎯 STATUS CHANGE: QStash automations triggered successfully for ${newStatus}`);
          } catch (automationError) {
            console.error(`❌ STATUS CHANGE: Failed to trigger QStash automations:`, automationError);
            // Don't fail the entire status change process if automation fails
          }
        }
      } else {
        // For other status changes (like completed → scheduled), we still need to handle feedback cleanup
        try {
          console.log(`[STATUS] 🔄 Processing status change for non-automation triggers: ${previousStatus} → ${newStatus}`);
          const processingResult = await processBookingStatusChange(localBooking.id, newStatus, previousStatus);
          
          if (processingResult.success) {
            console.log(`[STATUS] ✅ Status change processing completed successfully`);
          } else {
            console.warn(`[STATUS] ⚠️ Status change processing had issues: ${processingResult.error}`);
          }
        } catch (error) {
          console.error(`[STATUS] ❌ Error in status change processing:`, error);
        }
      }

      onUpdate();
    } catch (err) {
      console.error('[STATUS] ❌ Error updating status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    try {
      setStatusLoading(true);
      setError(null);

      if (!localBooking?.id) {
        throw new Error('No booking ID found');
      }

      // If the booking is already cancelled, schedule it
      const newStatus = localBooking.status === 'cancelled' ? 'scheduled' : 'cancelled';
      
      console.log(`[CANCEL] Setting booking ${localBooking.id} to ${newStatus}`);

      const result = await updateBooking(localBooking.id, { 
        status: newStatus,
        // Clear completed_at when scheduling a cancelled booking
        completed_at: newStatus === 'scheduled' ? null : localBooking.completed_at
      });

      if (!result) {
        throw new Error(`Failed to ${newStatus === 'cancelled' ? 'cancel' : 'schedule'} booking`);
      }

      // If the booking was just canceled (not scheduled), process the cancellation
      if (newStatus === 'cancelled') {
        try {
          // Process the cancellation to trigger OLD automation system
          console.log(`[CANCEL] Triggering OLD cancellation automation processing`);
          const processingResult = await processCanceledBooking(localBooking.id);
          
          if (processingResult.success) {
            console.log('[CANCEL] OLD automation processing completed successfully');
          } else {
            console.warn('[CANCEL] OLD automation processing had issues:', processingResult.error);
            // Don't throw error here - we still want to show the booking as canceled
          }
        } catch (error) {
          console.error('[CANCEL] Error in OLD automation processing:', error);
          // Don't throw error here - we still want to show the booking as canceled even if notifications fail
        }

        // 🎯 Also trigger NEW QStash-based automations for canceled bookings
        if (selectedBusiness?.id && localBooking) {
          try {
            console.log(`[CANCEL] 🚀 Triggering NEW QStash automations for canceled booking`);
            
            // Prepare booking data for automation trigger
            const realBookingData = {
              booking_id: localBooking.id,
              booking_number: localBooking.booking_number,
              title: localBooking.title,
              customer: {
                full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
                phone: localBooking.customer?.phone || undefined,
                email: localBooking.customer?.email || undefined
              },
              schedule: {
                drop_off_date: localBooking.drop_off_date || '',
                drop_off_time: localBooking.drop_off_time || '',
                start_date: localBooking.start_date || '',
                start_time: localBooking.start_time || '',
                end_date: localBooking.end_date || '',
                end_time: localBooking.end_time || ''
              },
              services: (localBooking.services || []).map(service => ({
                name: service.name,
                price: service.price,
                duration_minutes: service.duration_minutes || 60,
                vehicle: {
                  make: service.vehicle_make || '',
                  model: service.vehicle_model || '',
                  year: service.vehicle_year || 0,
                  color: service.vehicle_color || ''
                }
              })),
              vehicles: (() => {
                const vehicles = (localBooking.services || [])
                  .filter(service => service.vehicle_make || service.vehicle_model)
                  .map(service => ({
                    make: service.vehicle_make || '',
                    model: service.vehicle_model || '', 
                    year: service.vehicle_year || 0,
                    color: service.vehicle_color || '',
                    display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
                  }));
                
                // Remove duplicates based on make/model/year combination
                const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                  index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
                );
                
                return uniqueVehicles;
              })(),
              grand_total: (localBooking as any).grand_total || localBooking.subtotal || 0,
              status: 'cancelled',
              resources: (localBooking.resources || []).map(bookingResource => ({
                name: `${bookingResource.resource?.first_name || ''} ${bookingResource.resource?.last_name || ''}`.trim(),
                email: bookingResource.resource?.email || '',
                phone: bookingResource.resource?.phone || ''
              })),
              source: 'cancel_action'
            };

            console.log(`🎯 CANCEL: Triggering QStash automations with data:`, realBookingData);
            await triggerBookingAutomations(selectedBusiness.id, 'canceled', realBookingData);
            console.log(`🎯 CANCEL: QStash automations triggered successfully for canceled booking`);
          } catch (automationError) {
            console.error(`❌ CANCEL: Failed to trigger QStash automations:`, automationError);
            // Don't fail the entire cancellation process if automation fails
          }
        }
      }

      // Update local state
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: newStatus,
          completed_at: newStatus === 'scheduled' ? null : prev.completed_at
        } as Booking;
      });

      onUpdate();
    } catch (err) {
      console.error('[CANCEL] Error updating booking status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking status');
      throw err;
    } finally {
      setStatusLoading(false);
    }
  };

  const handleScheduleChange = async (startDate: string, startTime: string, endDate: string, endTime: string) => {
    if (!localBooking?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Updating booking schedule:', {
        bookingId: localBooking.id,
        startDate,
        startTime,
        endDate,
        endTime
      });
      
      await updateBooking(localBooking.id, {
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime
      });
      
      // Update local booking state
      setLocalBooking(prev => prev ? {
        ...prev,
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime
      } : null);
      
      // Notify parent component to refresh calendar
      onUpdate();
      
      console.log('Booking schedule updated successfully');
    } catch (err) {
      console.error('Error updating booking schedule:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletedAtChange = async (completedAt: string) => {
    if (!localBooking?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Updating booking completed at timestamp:', {
        bookingId: localBooking.id,
        completedAt
      });
      
      // Update the booking's completed_at timestamp
      await updateBooking(localBooking.id, {
        completed_at: completedAt
      });
      
      // Also update all related flat rate items in payroll_page table
      console.log('Updating payroll flat rate items completed_at timestamp:', {
        bookingId: localBooking.id,
        completedAt
      });
      
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }
      
      const { error: payrollError } = await supabase
        .from('payroll_page')
        .update({ completed_at: completedAt })
        .eq('booking_id', localBooking.id)
        .eq('entry_type', 'flat_rate');
      
      if (payrollError) {
        console.error('Error updating payroll entries:', payrollError);
        throw new Error(`Failed to update payroll entries: ${payrollError.message}`);
      }
      
      // Update local booking state
      setLocalBooking(prev => prev ? {
        ...prev,
        completed_at: completedAt
      } : null);
      
      // Notify parent component to refresh calendar
      onUpdate();
      
      console.log('Booking and payroll flat rate items completed at timestamp updated successfully');
    } catch (err) {
      console.error('Error updating booking completed at timestamp:', err);
      setError(err instanceof Error ? err.message : 'Failed to update completion timestamp');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!localBooking) return;

    try {
      setLoading(true);
      setError(null);
      
      await deleteBooking(localBooking.id);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error deactivating booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate booking');
    } finally {
      setLoading(false);
    }
  };

  const handleColorUpdate = async (color: string, secondaryColor?: string) => {
    if (!localBooking?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Update the booking color in the database
      await updateBooking(localBooking.id, {
        color: color,
        secondary_color: secondaryColor || null
      });

      // Update local state immediately
      setLocalBooking(prev => prev ? {
        ...prev,
        color: color,
        secondary_color: secondaryColor || null
      } : null);

      // Refresh the calendar/list view
      onUpdate();

      // Close the booking color picker modal
      setIsBookingColorPickerModalOpen(false);
    } catch (err) {
      console.error('Error updating booking color:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking color');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  if (!localBooking) return null;
  
  // Detect if we're on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // Modal content to render
  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] booking-info-modal ios-modal-fix ${isOpen ? '' : 'pointer-events-none'}`}
      aria-modal="true"
      role="dialog"
      style={{
        // Add will-change to hint browser to use hardware acceleration
        willChange: 'transform, opacity',
        // Force modal to be on top with a higher z-index
        zIndex: 99999,
        // Force 3D rendering to create new stacking context
        transform: 'translateZ(0) scale(1.0, 1.0)',
        // Ensure modal is rendered on top of everything
        isolation: 'isolate',
        // iOS specific overrides
        WebkitOverflowScrolling: 'touch',
        WebkitTransform: 'translateZ(0) scale(1.0, 1.0)',
        WebkitBackfaceVisibility: 'hidden',
        // Position relative to viewport for iOS
        position: 'fixed',
        // Add more specific iOS fixes
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: '100%',
        width: '100%'
      }}
    >
      <div 
        className={`fixed inset-0 z-[99900] transition-opacity duration-300 ios-modal-backdrop ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onTransitionEnd={handleTransitionEnd}
        style={{
          position: 'fixed',
          zIndex: 99900,
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
        }}
      >
        <div 
          className={`fixed inset-0 bg-white transform transition-transform duration-300 ease-out ios-modal-content ${
            isOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ 
            // Force 3D transforms for hardware acceleration and new stacking context
            transform: isOpen ? 'translate3d(0, 0, 0) scale(1.0)' : 'translate3d(0, 100%, 0)',
            // Add webkit specific properties for iOS
            WebkitTransform: isOpen ? 'translate3d(0, 0, 0) scale(1.0)' : 'translate3d(0, 100%, 0)',
            WebkitBackfaceVisibility: 'hidden',
            // Ensure the modal content has higher z-index
            zIndex: 99950,
            // Position explicitly
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }}
        >
          <div className="h-screen flex flex-col" style={{ 
            paddingTop: '0px',
            // Fix for iOS stacking context issues
            position: 'relative',
            zIndex: 9999
          }}>
            <BookingHeader
              bookingNumber={localBooking.booking_number || 0}
              title={localBooking.title || 'Untitled Booking'}
              status={localBooking.status}
              onClose={onClose}
              onEdit={() => setIsEditTitleModalOpen(true)}
              onEditColor={() => {
                console.log('Edit Color clicked - opening booking color picker modal');
                setIsBookingColorPickerModalOpen(true);
              }}
              onDelete={async () => {
                try {
                  if (!localBooking?.id) {
                    throw new Error('No booking ID found');
                  }
                  
                  console.log('Attempting to delete booking:', {
                    bookingId: localBooking.id,
                    bookingNumber: localBooking.booking_number
                  });

                  // 🎯 Trigger NEW QStash-based automations BEFORE deleting the booking
                  if (selectedBusiness?.id && localBooking) {
                    try {
                      console.log(`[DELETE] 🚀 Triggering NEW QStash automations for deleted booking`);
                      
                      // Prepare booking data for automation trigger
                      const realBookingData = {
                        booking_id: localBooking.id,
                        booking_number: localBooking.booking_number,
                        title: localBooking.title,
                        customer: {
                          full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
                          phone: localBooking.customer?.phone || undefined,
                          email: localBooking.customer?.email || undefined
                        },
                        schedule: {
                          drop_off_date: localBooking.drop_off_date || '',
                          drop_off_time: localBooking.drop_off_time || '',
                          start_date: localBooking.start_date || '',
                          start_time: localBooking.start_time || '',
                          end_date: localBooking.end_date || '',
                          end_time: localBooking.end_time || ''
                        },
                        services: (localBooking.services || []).map(service => ({
                          name: service.name,
                          price: service.price,
                          duration_minutes: service.duration_minutes || 60,
                          vehicle: {
                            make: service.vehicle_make || '',
                            model: service.vehicle_model || '',
                            year: service.vehicle_year || 0,
                            color: service.vehicle_color || ''
                          }
                        })),
                        vehicles: (() => {
                          const vehicles = (localBooking.services || [])
                            .filter(service => service.vehicle_make || service.vehicle_model)
                            .map(service => ({
                              make: service.vehicle_make || '',
                              model: service.vehicle_model || '', 
                              year: service.vehicle_year || 0,
                              color: service.vehicle_color || '',
                              display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
                            }));
                          
                          // Remove duplicates based on make/model/year combination
                          const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                            index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
                          );
                          
                          return uniqueVehicles;
                        })(),
                        grand_total: (localBooking as any).grand_total || localBooking.subtotal || 0,
                        status: 'deleted',
                        resources: (localBooking.resources || []).map(bookingResource => ({
                          name: `${bookingResource.resource?.first_name || ''} ${bookingResource.resource?.last_name || ''}`.trim(),
                          email: bookingResource.resource?.email || '',
                          phone: bookingResource.resource?.phone || ''
                        })),
                        source: 'delete_action'
                      };

                      console.log(`🎯 DELETE: Triggering QStash automations with data:`, realBookingData);
                      await triggerBookingAutomations(selectedBusiness.id, 'deleted', realBookingData);
                      console.log(`🎯 DELETE: QStash automations triggered successfully for deleted booking`);
                    } catch (automationError) {
                      console.error(`❌ DELETE: Failed to trigger QStash automations:`, automationError);
                      // Don't fail the entire deletion process if automation fails
                    }
                  }
                  
                  await deleteBooking(localBooking.id);
                  
                  console.log('Booking deleted successfully');
                  onUpdate();
                  onClose();
                } catch (err) {
                  console.error('Error deleting booking:', err);
                  const errorMessage = err instanceof Error ? err.message : 'Failed to delete booking';
                  setError(errorMessage);
                  throw new Error(errorMessage);
                }
              }}
              onCancel={handleCancelBooking}
              createdAt={localBooking.created_at}
              completedAt={localBooking.completed_at}
              onStatusChange={localBooking.status === 'cancelled' ? undefined : handleStatusChange}
              onCompletedAtChange={handleCompletedAtChange}
              readOnly={readOnly || localBooking.status === 'cancelled'}
              isMobile={isMobile}
              bookingId={localBooking.id}
              businessId={localBooking.business_id}
              canEdit={canAccess.editBookingDetails}
            />

            {/* Conditional rendering based on mobile/desktop */}
            {isMobile ? (
              <MobileTabStatusHeader
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            ) : (
              <>
                <BookingStatusBar
                  status={localBooking.status}
                  createdAt={localBooking.created_at}
                  completedAt={localBooking.completed_at}
                  onStatusChange={localBooking.status === 'cancelled' ? undefined : handleStatusChange}
                  onCompletedAtChange={handleCompletedAtChange}
                  readOnly={readOnly || localBooking.status === 'cancelled'}
                  bookingId={localBooking.id}
                  businessId={localBooking.business_id}
                  canEdit={canAccess.editBookingDetails}
                />

                <BookingTabs activeTab={activeTab} onTabChange={setActiveTab} />
              </>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-[#f6f6f7]">
              <div className={`mx-auto py-4 ${isMobile ? 'px-3 max-w-full' : 'px-6 max-w-5xl'}`}>
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                <BookingContent
                  activeTab={activeTab}
                  booking={localBooking}
                  isSelectMaterialModalOpen={isSelectMaterialModalOpen}
                  setIsSelectMaterialModalOpen={setIsSelectMaterialModalOpen}
                  onEditCustomer={() => setIsEditCustomerModalOpen(true)}
                  onEditVehicle={(vehicleId) => {
                    const vehicle = localBooking.services?.find(s => s.vehicle_id === vehicleId);
                    if (vehicle) {
                      setSelectedVehicle({
                        id: vehicleId,
                        make: vehicle.vehicle_make,
                        model: vehicle.vehicle_model,
                        year: vehicle.vehicle_year,
                        color: vehicle.vehicle_color
                      });
                      setIsEditVehicleModalOpen(true);
                    }
                  }}
                  onAddService={(vehicleId) => {
                    setCurrentVehicleId(vehicleId);
                    if (vehicleId) {
                      const vehicle = localBooking.services?.find(s => s.vehicle_id === vehicleId);
                      if (vehicle) {
                        setCurrentVehicle({
                          id: vehicleId,
                          make: vehicle.vehicle_make,
                          model: vehicle.vehicle_model,
                          year: vehicle.vehicle_year,
                          color: vehicle.vehicle_color
                        });
                      }
                    }
                    setIsSelectServiceModalOpen(true);
                  }}
                  onRemoveResource={handleRemoveResource}
                  onAddResource={() => setIsSelectResourceModalOpen(true)}
                  onApplyDiscount={() => setIsApplyDiscountModalOpen(true)}
                  onDeleteDiscount={async (discountId) => {
                    try {
                      setLoading(true);
                      setError(null);
                      await deleteDiscount(discountId);
                      await refreshBooking();
                    } catch (err) {
                      console.error('Error removing discount:', err);
                      setError(err instanceof Error ? err.message : 'Failed to remove discount');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onUpdate={refreshBooking}
                  onError={setError}
                  onAddressUpdate={handleAddressUpdate}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ModifyCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => {
          setIsEditCustomerModalOpen(false);
          // Refresh booking data after customer update
          if (localBooking?.id) {
            loadBookingData(localBooking.id);
          }
        }}
        onSuccess={(updatedCustomer: Customer) => {
          // Update local state
          setLocalBooking(prev => prev ? {
            ...prev,
            customer: updatedCustomer
          } : null);
          
          // Refresh parent component
          onUpdate();
          
          setIsEditCustomerModalOpen(false);
        }}
        onError={(error: string | null) => {
          if (error) {
            setError(error);
          }
        }}
        customer={localBooking.customer || null}
        bookingId={localBooking.id}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onSelect={handleAddResource}
        selectedResources={selectedResources}
      />

      <EditVehicleModal
        isOpen={isEditVehicleModalOpen}
        onClose={() => {
          setIsEditVehicleModalOpen(false);
          // Refresh booking data after vehicle update
          if (localBooking?.id) {
            loadBookingData(localBooking.id);
          }
        }}
        vehicle={selectedVehicle}
        onUpdate={refreshBooking}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={async (vehicle) => {
          setCurrentVehicle(vehicle);
          setCurrentVehicleId(vehicle.id);
          setIsSelectVehicleModalOpen(false);
          setIsSelectServiceModalOpen(true);
        }}
        vehicles={localBooking.customer?.vehicles || []}
        selectedVehicles={localBooking.services?.map(s => ({
          id: s.vehicle_id,
          make: s.vehicle_make || '',
          model: s.vehicle_model || '',
          year: s.vehicle_year || new Date().getFullYear()
        })) || []}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setCurrentVehicleId(null);
          setCurrentVehicle(null);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          try {
            if (!localBooking?.id || !currentVehicleId) {
              throw new Error('Invalid booking or vehicle');
            }

            // Calculate tax amounts with proper rounding for currency
            const taxRate = service.tax_exempt ? 0 : 5; // Default tax rate
            const taxAmount = service.tax_exempt ? 0 : Math.round((priceOption.price * taxRate / 100) * 100) / 100;
            const totalAmount = Math.round((priceOption.price + taxAmount) * 100) / 100;

            // Insert new service directly into booking_services table
            const { error: serviceError } = await supabase
              .from('booking_services')
              .insert({
                business_id: selectedBusiness?.id,
                booking_id: localBooking.id,
                service_id: service.id,
                vehicle_id: currentVehicleId,
                name: service.name,
                description: service.description || '',
                price: priceOption.price,
                tax_exempt: service.tax_exempt || false,
                tax_rate: taxRate,
                tax_amount: taxAmount,
                total_amount: totalAmount,
                duration_minutes: priceOption.duration_minutes,
                status: 'pending'
              });

            if (serviceError) throw serviceError;
            
            // Refresh booking data immediately
            await refreshBooking();
            
            // Close modal
            setIsSelectServiceModalOpen(false);
          } catch (err) {
            console.error('Error adding service:', err);
            setError(err instanceof Error ? err.message : 'Failed to add service');
          }
        }}
        bookingId={localBooking?.id}
        vehicleId={currentVehicleId}
      />

      <EditTitleModal
        isOpen={isEditTitleModalOpen}
        onClose={() => setIsEditTitleModalOpen(false)}
        bookingId={localBooking.id}
        currentTitle={localBooking.title}
        onUpdate={refreshBooking}
      />

      <BookingColorPickerModal
        isOpen={isBookingColorPickerModalOpen}
        onClose={() => setIsBookingColorPickerModalOpen(false)}
        onColorSelect={handleColorUpdate}
        currentColor={localBooking.color || '#3b82f6'}
        currentSecondaryColor={localBooking.secondary_color || undefined}
        businessId={localBooking.business_id}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            setError(null);
            await deleteBooking(localBooking.id);
            onUpdate();
            onClose();
          } catch (err) {
            console.error('Error deleting booking:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete booking');
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Booking"
        message={`Are you sure you want to delete booking #${localBooking.booking_number}? This action cannot be undone.`}
        loading={deleteLoading}
      />

      <ApplyDiscountModal
        isOpen={isApplyDiscountModalOpen}
        onClose={() => setIsApplyDiscountModalOpen(false)}
        bookingId={localBooking.id}
        bookingTotal={localBooking.total_amount}
        onApply={refreshBooking}
      />

      <SelectMaterialModal
        isOpen={isSelectMaterialModalOpen}
        onClose={() => setIsSelectMaterialModalOpen(false)}
        onAdd={async (material, quantity) => {
          try {
            // Insert new booking material
            const { error: insertError } = await supabase
              .from('booking_materials')
              .insert({
                booking_id: localBooking.id,
                material_id: material.id,
                material_name: material.name,
                material_description: material.description,
                quantity,
                unit: material.unit,
                cost: material.cost,
                total_cost: material.cost * quantity
              });

            if (insertError) throw insertError;

            await refreshBooking();
            setIsSelectMaterialModalOpen(false);
          } catch (err) {
            console.error('Error adding material:', err);
            setError(err instanceof Error ? err.message : 'Failed to add material');
          }
        }}
        businessId={selectedBusiness?.id}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={async (vehicle) => {
          // Update customer data to get the new vehicle
          const updatedCustomer = await getCustomer(localBooking.customer?.id || '');
          if (updatedCustomer) {
            setLocalBooking(prev => prev ? {
              ...prev,
              customer: updatedCustomer
            } : null);
          }
          
          // Set the new vehicle as current and open service selection
          setCurrentVehicle(vehicle);
          setCurrentVehicleId(vehicle.id);
          setIsCreateVehicleModalOpen(false);
          setIsSelectServiceModalOpen(true);
        }}
        customerId={localBooking.customer?.id || ''}
      />
    </div>
  );
  
  // If on iOS, render modal in the portal
  if (isIOS && modalPortalRef.current) {
    // Use portal rendering
    
    // Dynamically render using the portal
    if (typeof document !== 'undefined') {
      // Only run on client side
      const portalRoot = modalPortalRef.current;
      if (portalRoot) {
        return createPortal(modalContent, portalRoot);
      }
    }
  }
  
  // Default render directly in the component tree
  return modalContent;
}